import { ApiError } from '@platform/api-client';
import { z } from 'zod';
import type { Customer, CustomerBody } from './customers.ts';

export const CUSTOMER_LIMITS = {
  name: 200,
  phone: 32,
  email: 254,
  address: 500,
  area: 100,
  postalCode: 20,
  notes: 2000,
} as const;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export const customerFormSchema = z.object({
  name: z.string().trim().min(1).max(CUSTOMER_LIMITS.name),
  phone: z.string().trim().min(1).max(CUSTOMER_LIMITS.phone),
  secondPhone: z.string().max(CUSTOMER_LIMITS.phone),
  email: z
    .string()
    .trim()
    .max(CUSTOMER_LIMITS.email)
    .refine((email) => email === '' || EMAIL.test(email)),
  address: z.string().max(CUSTOMER_LIMITS.address),
  governorate: z.string().max(CUSTOMER_LIMITS.area),
  city: z.string().max(CUSTOMER_LIMITS.area),
  postalCode: z.string().max(CUSTOMER_LIMITS.postalCode),
  notes: z.string().max(CUSTOMER_LIMITS.notes),
});

export type CustomerFormValues = z.infer<typeof customerFormSchema>;

export type CustomerField = keyof CustomerFormValues;

const VALIDATION_FAILED = 'VALIDATION_FAILED';

const isCustomerField = (value: unknown): value is CustomerField =>
  typeof value === 'string' && value in customerFormSchema.shape;

export const refusedFieldOf = (error: unknown): CustomerField | null => {
  if (!(error instanceof ApiError) || error.code !== VALIDATION_FAILED) return null;
  const field = error.details['field'];
  return isCustomerField(field) ? field : null;
};

const orNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

export type CustomerPrefill = { readonly name: string; readonly phone: string };

export const NO_PREFILL: CustomerPrefill = { name: '', phone: '' };

const PHONE_LIKE = /^\+?[\d\s.-]+$/u;

export const prefillOf = (typed: string): CustomerPrefill => {
  const text = typed.trim();
  return PHONE_LIKE.test(text) ? { name: '', phone: text } : { name: text, phone: '' };
};

export const formValuesOf = (
  customer: Customer | null,
  prefill: CustomerPrefill = NO_PREFILL,
): CustomerFormValues => ({
  name: customer?.name ?? prefill.name,
  phone: customer?.phone ?? prefill.phone,
  secondPhone: customer?.secondPhone ?? '',
  email: customer?.email ?? '',
  address: customer?.address ?? '',
  governorate: customer?.governorate ?? '',
  city: customer?.city ?? '',
  postalCode: customer?.postalCode ?? '',
  notes: customer?.notes ?? '',
});

export const customerBodyOf = (values: CustomerFormValues): CustomerBody => ({
  name: values.name.trim(),
  phone: values.phone.trim(),
  secondPhone: orNull(values.secondPhone),
  email: orNull(values.email),
  address: orNull(values.address),
  governorate: orNull(values.governorate),
  city: orNull(values.city),
  postalCode: orNull(values.postalCode),
  notes: orNull(values.notes),
});
